/**
 * Auth Callback Page
 * Handles Supabase authentication redirects (password reset, magic links, etc.)
 *
 * For recovery (password reset) flow:
 * - Checks if user is pending (needs registration) or complete (needs password reset)
 * - Pending users go to /registration/complete
 * - Complete users go to /reset-password
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { apiRequestProtected } from '../utils/axios'
import { LocalStorageItem } from '../common/constants'
import { useAuthContext } from '../contexts/AuthContext'

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')
  const { completeSignIn } = useAuthContext()

  useEffect(() => {
    async function handleCallback() {
      const type = searchParams.get('type')

      // Get tokens from URL hash (Supabase puts them there)
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const accessToken = hashParams.get('access_token')
      const refreshToken = hashParams.get('refresh_token')

      if (accessToken && refreshToken) {
        // Set the session
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        if (sessionError) {
          console.error('❌ Failed to set session:', sessionError)
          setError('Failed to authenticate. Please try again.')
          return
        }

        console.log('✅ Session established successfully')

        // Handle password recovery flow
        if (type === 'recovery') {
          // Check if user needs registration or just password reset
          try {
            const response = await apiRequestProtected('GET', '/auth/registration-status')

            if (response.success && response.data) {
              if (response.data.needsRegistration) {
                // Pending user - redirect to complete registration
                console.log('📝 User needs to complete registration')
                localStorage.setItem(LocalStorageItem.registrationPending, 'true')
                navigate('/registration/complete', { replace: true })
              } else {
                // Complete user - redirect to password reset page
                console.log('🔑 User needs to reset password')
                navigate('/reset-password', { replace: true })
              }
              return
            }
          } catch (error) {
            console.error('❌ Failed to check registration status:', error)
            // Fallback to registration complete page on error
            localStorage.setItem(LocalStorageItem.registrationPending, 'true')
            navigate('/registration/complete', { replace: true })
            return
          }
        }

        // Default: route by role. Partner -> /partner, admin -> /admin, else /map.
        const { data: userRes } = await supabase.auth.getUser()
        const u = userRes?.user
        const isAdmin =
          u?.user_metadata?.role === 'admin' ||
          u?.user_metadata?.roleId === 1 ||
          u?.app_metadata?.role === 'admin'
        const isPartner =
          u?.user_metadata?.role === 'partner' ||
          u?.user_metadata?.role === 'PARTNER' ||
          u?.user_metadata?.roleId === 2 ||
          u?.app_metadata?.role === 'partner'

        // SIGNED_IN fired while we were still on this transient path, so the
        // user is not signed in yet as far as the app is concerned.
        if (u?.email) {
          completeSignIn(u.email, accessToken)
        }

        if (isAdmin) {
          navigate('/admin', { replace: true })
        } else if (isPartner) {
          navigate('/partner', { replace: true })
        } else {
          navigate('/map', { replace: true })
        }
      } else {
        console.error('❌ Missing tokens in callback URL')
        setError('Invalid authentication link. Please request a new one.')
      }
    }

    handleCallback()
  }, [navigate, searchParams, completeSignIn])

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500 mb-2">
              Need a new link to complete registration?
            </p>
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing authentication...</p>
      </div>
    </div>
  )
}
